if (!customElements.get('lookbook-collage')) {
  class LookbookCollage extends HTMLElement {
    connectedCallback() {
      this.openCard = null;
      this.onClick = this.onClick.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.addEventListener('click', this.onClick);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);
      this.scroller = this.querySelector('[data-lookbook-scroller]');
      this.prev = this.querySelector('[data-lookbook-dir="prev"]');
      this.next = this.querySelector('[data-lookbook-dir="next"]');
      this.onScroll = () => this.updateArrows();
      this.scroller?.addEventListener('scroll', this.onScroll, { passive: true });
      this.updateArrows();
      requestAnimationFrame(() => this.updateArrows());
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
      this.scroller?.removeEventListener('scroll', this.onScroll);
    }

    onClick(event) {
      const arrow = event.target.closest('[data-lookbook-dir]');
      if (arrow && this.contains(arrow)) {
        event.preventDefault();
        if (arrow.disabled) return;
        this.close();
        this.scrollByCard(arrow.dataset.lookbookDir === 'next' ? 1 : -1);
        return;
      }

      const closeBtn = event.target.closest('[data-lookbook-close]');
      if (closeBtn && this.contains(closeBtn)) {
        event.preventDefault();
        this.close();
        return;
      }

      const trigger = event.target.closest('[data-lookbook-open]');
      if (!trigger || !this.contains(trigger)) return;

      event.preventDefault();
      const card = trigger.closest('.lookbook__card');
      if (!card) return;
      if (card.classList.contains('is-open')) this.close();
      else this.open(card);
    }

    onDocumentClick(event) {
      const closeBtn = event.target.closest('[data-lookbook-close]');
      if (closeBtn && this.openCard && this.openCard.contains(closeBtn)) {
        event.preventDefault();
        this.close();
        return;
      }
      if (!this.classList.contains('is-open')) return;
      if (event.target.closest('.lookbook__card.is-open')) return;
      this.close();
    }

    onKeydown(event) {
      if (event.key === 'Escape') this.close();
    }

    open(card) {
      this.close();

      const panel = card.querySelector('[data-lookbook-panel]');
      const trigger = card.querySelector('[data-lookbook-open]');
      const isMobile = window.matchMedia('(max-width: 749px)').matches;

      if (!isMobile && this.isRightmost(card)) {
        card.classList.add('lookbook__card--slide-left');
      }

      this.openCard = card;

      if (panel) {
        panel.inert = false;
        panel.setAttribute('aria-hidden', 'false');
      }
      trigger?.setAttribute('aria-expanded', 'true');

      if (isMobile) this.pin(card);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (this.openCard !== card) return;
          card.classList.add('is-open');
          this.classList.add('is-open');
          if (!isMobile) panel?.querySelector('[data-lookbook-close]')?.focus({ preventScroll: true });
        });
      });
    }

    close() {
      const card = this.openCard || this.querySelector('.lookbook__card.is-open') || document.querySelector('.lookbook__card.is-open.is-pinned');
      if (!card) {
        this.classList.remove('is-open');
        this.openCard = null;
        return;
      }

      const trigger = card.querySelector('[data-lookbook-open]');
      const panel = card.querySelector('[data-lookbook-panel]');
      const restoreFocus = document.activeElement && card.contains(document.activeElement);
      const clearSlideLeft = () => {
        if (card.classList.contains('is-open')) return;
        card.classList.remove('lookbook__card--slide-left');
      };

      card.classList.remove('is-open');
      if (panel) {
        panel.inert = true;
        panel.setAttribute('aria-hidden', 'true');
      }
      trigger?.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
      this.openCard = null;
      if (restoreFocus && !card.classList.contains('is-pinned')) trigger?.focus({ preventScroll: true });

      if (card.classList.contains('is-pinned')) {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const release = (event) => {
          if (event && (event.target !== card || event.propertyName !== 'transform')) return;
          card.removeEventListener('transitionend', release);
          window.clearTimeout(card._releaseTimer);
          this.unpin(card);
        };
        if (reduce) {
          this.unpin(card);
        } else {
          card.addEventListener('transitionend', release);
          card._releaseTimer = window.setTimeout(() => release(), 1000);
        }
      }

      if (card.classList.contains('lookbook__card--slide-left')) {
        const face = card.querySelector('.lookbook__face');
        if (face) face.addEventListener('transitionend', clearSlideLeft, { once: true });
        window.setTimeout(clearSlideLeft, 400);
      }
    }

    pin(card) {
      const rect = card.getBoundingClientRect();
      const holder = document.createElement('div');
      holder.className = 'lookbook__holder';
      holder.setAttribute('aria-hidden', 'true');
      holder.style.width = `${rect.width}px`;
      holder.style.height = `${rect.height}px`;
      holder.style.flex = '0 0 auto';
      card.before(holder);
      card._holder = holder;
      document.body.append(card);
      card.classList.add('is-pinned');
      card.style.top = `${rect.top}px`;
      card.style.left = `${rect.left}px`;
      card.style.width = `${rect.width}px`;
      card.style.height = `${rect.height}px`;
    }

    unpin(card) {
      if (!card.classList.contains('is-pinned')) return;
      const holder = card._holder;
      card.classList.remove('is-pinned');
      card.style.top = '';
      card.style.left = '';
      card.style.width = '';
      card.style.height = '';
      if (holder && holder.isConnected) holder.replaceWith(card);
      card._holder = null;
    }

    isRightmost(card) {
      const cards = [...this.querySelectorAll('.lookbook__card')];
      return cards.length > 1 && cards[cards.length - 1] === card;
    }

    scrollByCard(direction) {
      if (!this.scroller) return;
      const card = this.scroller.querySelector('.lookbook__card');
      if (!card) return;
      const gap = parseFloat(getComputedStyle(this.scroller).columnGap) || 0;
      const step = card.getBoundingClientRect().width + gap;
      this.scroller.scrollBy({ left: step * direction, behavior: 'smooth' });
    }

    updateArrows() {
      if (!this.scroller || !this.prev || !this.next) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.scroller;
      const atStart = scrollLeft <= 4;
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 4;
      this.setArrow(this.prev, atStart);
      this.setArrow(this.next, atEnd);
    }

    setArrow(button, disabled) {
      if (!button) return;
      button.classList.toggle('is-disabled', disabled);
      button.disabled = disabled;
    }
  }

  customElements.define('lookbook-collage', LookbookCollage);
}
